import React, { useState } from 'react';
import { Input, Select, TextArea } from '@/components/ui';
import type { UserNewFormData } from '../types/userNew.types';

interface WorkInfoStepProps {
  formData: UserNewFormData;
  updateField: (field: keyof UserNewFormData, value: any) => void;
  companyId: string;
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Quản trị viên' },
  { value: 'pm', label: 'Quản lý dự án' },
  { value: 'supervisor', label: 'Giám sát' },
  { value: 'technician', label: 'Kỹ thuật viên' },
  { value: 'staff', label: 'Nhân viên' },
  { value: 'viewer', label: 'Chỉ xem' },
];

const EDUCATION_OPTIONS = [
  { value: 'high_school', label: 'Trung học phổ thông' },
  { value: 'college', label: 'Cao đẳng' },
  { value: 'bachelor', label: 'Đại học' },
  { value: 'master', label: 'Thạc sĩ' },
  { value: 'doctor', label: 'Tiến sĩ' },
  { value: 'other', label: 'Khác' },
];

const CURRENT_YEAR = new Date().getFullYear();
const GRAD_YEAR_OPTIONS = Array.from({ length: 40 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { value: String(y), label: String(y) };
});

interface SkillTagInputProps {
  value: string[];
  onChange: (skills: string[]) => void;
}

function SkillTagInput({ value, onChange }: SkillTagInputProps) {
  const [input, setInput] = useState('');

  const addSkill = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  };

  const removeSkill = (skill: string) => {
    onChange(value.filter((s) => s !== skill));
  };

  return (
    <div className="space-y-2">
      <label className="block sm:text-sm text-xs text-slate-500 tracking-wider">
        Kỹ năng
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          className="flex-1 px-3 py-2.5 rounded-md border border-slate-100 bg-slate-50/50 sm:text-sm text-xs focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
          placeholder="Nhập kỹ năng và nhấn Enter..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
        />
        <button
          type="button"
          onClick={addSkill}
          className="px-3 py-2 rounded-md bg-emerald-50 text-emerald-700 sm:text-sm text-xs hover:bg-emerald-100 transition-colors border border-emerald-100"
        >
          Thêm
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {value.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 sm:text-sm text-xs border border-emerald-100"
            >
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="text-emerald-400 hover:text-emerald-700 transition-colors leading-none"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function WorkInfoStep({ formData, updateField }: WorkInfoStepProps) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Cột trái – 2/3 */}
      <div className="lg:col-span-2 space-y-6">

        {/* Section: Vai trò & Chức vụ */}
        <div className="bg-white rounded-md p-6 border border-slate-100">
          <h2 className="sm:text-sm text-xs font-medium text-slate-800 mb-6 tracking-wide">
            Vai trò &amp; Chức vụ
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Chức danh"
              placeholder="Ví dụ: Kỹ sư phần mềm"
              value={formData.job_title || ''}
              onChange={(e: any) => updateField('job_title', e.target.value)}
            />

            <Select
              label="Vai trò hệ thống"
              value={formData.role_code || 'pm'}
              onValueChange={(val: string) => updateField('role_code', val)}
              options={ROLE_OPTIONS}
            />

            <div className="md:col-span-2">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-100 flex items-start gap-2">
                <i className="ri-information-line text-slate-400 mt-0.5" />
                <p className="sm:text-sm text-xs text-slate-500">
                  Vai trò hệ thống kiểm soát quyền truy cập vào các tính năng trong phần mềm.
                  Vai trò thực tế trong tổ chức được nhập tại trường Chức danh.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Học vấn */}
        <div className="bg-white rounded-md p-6 border border-slate-100">
          <h2 className="sm:text-sm text-xs font-medium text-slate-800 mb-6 tracking-wide">
            Học vấn
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select
              label="Trình độ học vấn"
              value={formData.education_level || ''}
              onValueChange={(val: string) => updateField('education_level', val)}
              options={EDUCATION_OPTIONS}
            />

            <Select
              label="Năm tốt nghiệp"
              value={formData.graduation_year ? String(formData.graduation_year) : ''}
              onValueChange={(val: string) => updateField('graduation_year', Number(val))}
              options={GRAD_YEAR_OPTIONS}
            />

            <Input
              label="Trường học"
              placeholder="Tên trường đại học / cao đẳng"
              value={formData.university || ''}
              onChange={(e: any) => updateField('university', e.target.value)}
            />

            <Input
              label="Ngành học"
              placeholder="Ví dụ: Công nghệ thông tin"
              value={formData.major || ''}
              onChange={(e: any) => updateField('major', e.target.value)}
            />

            <div className="md:col-span-2">
              <SkillTagInput
                value={formData.skills || []}
                onChange={(skills) => updateField('skills', skills)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Cột phải – 1/3 */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-md p-6 border border-slate-100 sticky top-6">
          <h2 className="sm:text-sm text-xs font-medium text-slate-800 mb-6 tracking-wide">
            Thông tin bổ sung
          </h2>
          <div className="space-y-6">
            <TextArea
              label="Ghi chú thêm"
              placeholder="Kinh nghiệm nổi bật, thành tích, ghi chú từ HR..."
              value={formData.notes || ''}
              onChange={(e: any) => updateField('notes', e.target.value)}
              rows={8}
            />
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-100">
              <p className="sm:text-sm text-xs text-emerald-700 font-medium mb-1">Lưu ý</p>
              <p className="sm:text-sm text-xs text-emerald-600">
                Kỹ năng và học vấn giúp hệ thống gợi ý phân công công việc phù hợp.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
